import React, { useState } from 'react';
import { FaUser, FaEnvelope, FaCheck } from 'react-icons/fa';
import { getTranslation } from '../../utils/translations';
import './ProfileCompletion.css';

const ProfileCompletion = ({ 
  onComplete, 
  language, 
  phoneNumber, 
  countryCode,
  tempToken,
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = language === 'ar' ? 'يرجى إدخال الاسم' : 'Please enter your name';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = language === 'ar' ? 'الاسم يجب أن يكون أكثر من حرفين' : 'Name must be at least 2 characters';
    }

    // Email validation (optional but if provided must be valid)
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onComplete({
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        tempToken
      });
    } catch (error) {
      console.error('Profile completion error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhoneNumber = (phone, countryCode) => {
    if (!phone) return '';
    
    if (countryCode === '+966') {
      // Format Saudi number: +966 5X XXX XXXX
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.length === 9) {
        return `${countryCode} ${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
      }
    }
    
    return `${countryCode} ${phone}`;
  };

  return (
    <div className={`profile-completion-container ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="profile-header">
        <div className="profile-icon">
          <FaCheck />
        </div>
        <h3 className="profile-title">
          {getTranslation('completeProfile', language)}
        </h3>
        <p className="profile-subtitle">
          {language === 'ar' 
            ? `تم التحقق من رقم ${formatPhoneNumber(phoneNumber, countryCode)} بنجاح`
            : `Successfully verified ${formatPhoneNumber(phoneNumber, countryCode)}`
          }
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="profile-form">
        {/* Name Field */}
        <div className="form-group">
          <label className="form-label">
            <FaUser className="form-icon" />
            {getTranslation('fullName', language)} *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input ${errors.name ? 'error' : ''}`}
            placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
            disabled={isSubmitting || isLoading}
            autoComplete="name"
            autoFocus
          />
          {errors.name && (
            <div className="form-error">
              {errors.name}
            </div>
          )}
        </div>

        {/* Email Field */}
        <div className="form-group">
          <label className="form-label">
            <FaEnvelope className="form-icon" />
            {getTranslation('email', language)} ({language === 'ar' ? 'اختياري' : 'Optional'})
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني (اختياري)' : 'Enter your email (optional)'}
            disabled={isSubmitting || isLoading}
            autoComplete="email"
          />
          {errors.email && (
            <div className="form-error">
              {errors.email}
            </div>
          )}
          <div className="form-hint">
            {language === 'ar' 
              ? 'البريد الإلكتروني اختياري ولكن يساعد في استعادة الحساب'
              : 'Email is optional but helps with account recovery'
            }
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="profile-submit-btn"
          disabled={isSubmitting || isLoading || !formData.name.trim()}
        >
          {isSubmitting || isLoading ? (
            <div className="loading-spinner"></div>
          ) : (
            getTranslation('createAccount', language)
          )}
        </button>
      </form>

      {/* Info Section */}
      <div className="profile-info">
        <p>
          {language === 'ar' 
            ? 'بإنشاء الحساب، أنت توافق على شروط الاستخدام وسياسة الخصوصية'
            : 'By creating an account, you agree to our Terms of Service and Privacy Policy'
          }
        </p>
      </div>
    </div>
  );
};

export default ProfileCompletion;
